import z from 'zod';

import { UnityParagraph } from './Paragraph';
import { PlateTextElements, TextElementsCodec } from './Text';
import { CURRENT_VERSION, ElementId, UnityElementId, Version } from '../commons';
import { ELEMENT_TYPES, UNITY_ELEMENT_TYPES } from '../constants';

export const ListItemContent = z.object({
    type: z.literal(ELEMENT_TYPES.LIST_ITEM_CONTENT),
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: PlateTextElements,
});

/**
 *  Used by jsx.d.ts
 *  @public
 */
export type ListItemContentElement = z.output<typeof ListItemContent>;

export const PlateListItem = z.object({
    type: z.literal(ELEMENT_TYPES.LIST_ITEM),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: z.array(ListItemContent),
});

export type ListItemElement = z.output<typeof PlateListItem>;

export const UnityOrderedList = z.object({
    type: z.literal(ELEMENT_TYPES.ORDERED_LIST),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    items: z.array(UnityParagraph),
});
export type UnityOrderedList = z.infer<typeof UnityOrderedList>;

export const PlateOrderedList = z.object({
    type: z.literal(ELEMENT_TYPES.ORDERED_LIST),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: z.array(PlateListItem),
});
export type OrderedListElement = z.infer<typeof PlateOrderedList>;

export const OrderedListCodec = z.codec(UnityOrderedList, PlateOrderedList, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        ...(value.templateElement ? { data: { templateElement: value.templateElement } } : {}),
        children: value.items.map((item) => ({
            type: ELEMENT_TYPES.LIST_ITEM,
            id: item.elementId ?? crypto.randomUUID(),
            ...(item.templateElement ? { data: { templateElement: item.templateElement } } : {}),
            children: [
                {
                    type: ELEMENT_TYPES.LIST_ITEM_CONTENT,
                    ...(item.templateElement
                        ? { data: { templateElement: item.templateElement } }
                        : {}),
                    children: TextElementsCodec.decode(item.items),
                },
            ] satisfies [z.infer<typeof ListItemContent>],
        })),
    }),
    encode: (list) => ({
        type: list.type,
        elementId: list.id,
        version: CURRENT_VERSION,
        items: list.children.map((listItem) => ({
            type: UNITY_ELEMENT_TYPES.TEXT,
            elementId: listItem.id,
            version: CURRENT_VERSION,
            items: TextElementsCodec.encode(
                listItem.children.flatMap((listItemContent) => listItemContent.children),
            ),
        })),
        ...list.data,
    }),
});

export const UnityUnorderedList = z.object({
    type: z.literal(ELEMENT_TYPES.UNORDERED_LIST),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    items: z.array(UnityParagraph),
});
export type UnityUnorderedList = z.infer<typeof UnityUnorderedList>;

export const PlateUnorderedList = z.object({
    type: z.literal(ELEMENT_TYPES.UNORDERED_LIST),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: z.array(PlateListItem),
});
export type UnorderedListElement = z.infer<typeof PlateUnorderedList>;

export const UnorderedListCodec = z.codec(UnityUnorderedList, PlateUnorderedList, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        ...(value.templateElement ? { data: { templateElement: value.templateElement } } : {}),
        children: value.items.map((item) => ({
            type: ELEMENT_TYPES.LIST_ITEM,
            id: item.elementId ?? crypto.randomUUID(),
            ...(item.templateElement ? { data: { templateElement: item.templateElement } } : {}),
            children: [
                {
                    type: ELEMENT_TYPES.LIST_ITEM_CONTENT,
                    ...(item.templateElement
                        ? { data: { templateElement: item.templateElement } }
                        : {}),
                    children: TextElementsCodec.decode(item.items),
                },
            ] satisfies [z.infer<typeof ListItemContent>],
        })),
    }),
    encode: (list) => ({
        type: list.type,
        elementId: list.id,
        version: CURRENT_VERSION,
        items: list.children.map((listItem) => ({
            type: UNITY_ELEMENT_TYPES.TEXT,
            elementId: listItem.id,
            version: CURRENT_VERSION,
            items: TextElementsCodec.encode(
                listItem.children.flatMap((listItemContent) => listItemContent.children),
            ),
        })),
        ...list.data,
    }),
});

export const PlateList = z.discriminatedUnion('type', [PlateOrderedList, PlateUnorderedList]);
export type ListElement = z.infer<typeof PlateList>;
